import { Box, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TablePagination, Grid, IconButton } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import styled from "@emotion/styled";
import moment from "moment";
import { Add, Close, Description, DescriptionOutlined } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { ServiceOffer, serviceOptions, ServiceTypes } from "../../common/const";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { createNotes, getPastSession } from "../../api/sessionApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import NoteViewer from "../../common/components/NoteViewer";
import Dropzone from "react-dropzone";
import LoadingSpinner from "../../common/components/LoadingSpinner";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '400px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
}));

const SessionHistory = ({
    fromMentor,
    currentService,
    studentId,
    tutoringType,
    subService
}) => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [selectedSession, setSelectedSesion] = useState(null);
    const [addNotes, setAddNotes] = useState(false);
    const [viewNotes, setViewNotes] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [events, setEvents] = useState(null);
    const [fileNames, setFileNames] = useState([]);
    const [formData, setFormData] = useState({
        note: '',
        notes: '',
        recordedLinks: '',
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;
            setFormData((prev) => ({
                ...prev,
                [name]: file,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    const getEvents = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getPastSession(page + 1, rowsPerPage, (subService ? currentService === ServiceOffer.mentor ? currentService : ServiceOffer.skillCoach : ServiceTypes[tutoringType]) || currentService, user?.student_id || studentId, subService);
            setEvents(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id || studentId) {
            getEvents();
        }
    }, [page, rowsPerPage, tutoringType, subService, studentId, user]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={addNotes || viewNotes}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{viewNotes ? 'View Summary' : 'Add Notes'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddNotes(false);
                                    setViewNotes(false);
                                    setSelectedSesion(null);
                                    setFileNames([]);
                                    setFormData({
                                        note: '',
                                        notes: '',
                                        recordedLinks: '',
                                    });
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    viewNotes ? (
                        selectedSession?.sessionNotes.map((a) => (
                            <NoteViewer note={a} />
                        ))
                    ) : (
                        <Grid container spacing={2} mt={0.5} mb={1}>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="email"
                                    name="note"
                                    value={formData.note}
                                    onChange={handleInputChange}
                                    placeholder="Enter note"
                                    multiline
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="file"
                                    name="notes"
                                    value={formData.notes?.[0]?.name}
                                    onChange={handleInputChange}
                                    placeholder="Add Notes"
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="email"
                                    name="recordedLinks"
                                    value={formData.recordedLinks}
                                    onChange={handleInputChange}
                                    placeholder="Add Recording Link"
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <Box sx={{ width: '100%' }}>
                                    {fileNames.length > 0 ? (
                                        // Show selected files
                                        <Box sx={{ mt: 2 }}>
                                            {fileNames.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor: 'grey.300',
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        backgroundColor: 'background.paper'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Description color="primary" />
                                                        <Box>
                                                            <Typography variant="body1">{file.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(file.size / 1024).toFixed(2)} KB
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                        color="error"
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        // Show dropzone when no files
                                        <Dropzone
                                            onDrop={handleDrop}
                                        >
                                            {({ getRootProps, getInputProps, isDragActive }) => (
                                                <Box
                                                    {...getRootProps()}
                                                    sx={{
                                                        border: '2px dashed',
                                                        borderColor: '#D8DAEB',
                                                        borderRadius: 2,
                                                        padding: { xs: 3, md: 6 },
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        minHeight: 100,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <img src="/Images/sound.png" alt="upload" />
                                                    <Typography fontWeight={600} gutterBottom>
                                                        Drag and drop your files
                                                    </Typography>
                                                    <CommonButton
                                                        label="Select File"
                                                        color="Black"
                                                        background="White"
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        width="100px"
                                                        height="40px"
                                                        marginBottom="0px"
                                                        marginTop="10px"
                                                    />
                                                </Box>
                                            )}
                                        </Dropzone>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )
                }
                actionButtons={!viewNotes && (
                    <CommonButton
                        label={viewNotes ? "Close" : "Save"}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={async () => {
                            if (viewNotes) {
                                setViewNotes(false);
                            } else {
                                try {
                                    if (formData?.notes?.length > 0 || formData.note !== '' || formData.recordedLinks !== '' || fileNames?.length > 0) {
                                        dispatch(setLoading({
                                            loading: true
                                        }));
                                        const data = new FormData();
                                        if (formData.note !== '') {
                                            data.append('description', formData.note);
                                        }
                                        if (formData?.notes?.length > 0) {
                                            data.append('attachment', formData.notes[0]);
                                        }
                                        if (formData.recordedLinks !== '') {
                                            data.append('recording_link', formData.recordedLinks);
                                        }
                                        fileNames.map((a) => {
                                            data.append('other_doc', a);
                                        })
                                        data.append('mentor_profile_id', selectedSession?.mentor_profile_id);
                                        data.append('mentor_session_id', selectedSession?.mentor_session_id);
                                        data.append('student_id', studentId);
                                        const response = await createNotes(data);
                                        if (response.success) {
                                            showNotification(response.message, 'success');
                                            setAddNotes(false);
                                            setSelectedSesion(null);
                                            setFileNames([]);
                                            setFormData({
                                                note: '',
                                                notes: '',
                                                recordedLinks: '',
                                            });
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                            setPage(0);
                                            getEvents();
                                        }
                                    } else {
                                        showNotification('Please provide any detail for add note', 'error');
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }
                        }}
                    />
                )}
            />
            <CommonCard
                paddingBottom="0px"
                padding="0px"
                border={fromMentor && `1px solid #0000001A`}
                borderRadius={2}
                marginTop={fromMentor && "0px"}
                headerContent={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" padding={2} alignItems="center">
                            <Box>
                                <Typography fontWeight={600}>Past Session & Study Notes</Typography>
                                {!fromMentor && (
                                    <Typography fontSize={13} color="background.dark">Review notes and materials from previous sessions.</Typography>
                                )}
                            </Box>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Fragment>
                        <Box p={2} pb={0}>
                            <StyledTableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                            <Cell width="17%">Topic</Cell>
                                            <Cell width="11%">Date</Cell>
                                            {!fromMentor && (
                                                <>
                                                    <Cell width="15%">Tutor</Cell>
                                                    <Cell style={{ textAlign: 'left' }}>Tutor Notes</Cell>
                                                </>
                                            )}
                                            {fromMentor && (
                                                <Cell width="12%">Duration</Cell>
                                            )}
                                            <Cell width="15%">Action</Cell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {events?.data?.length > 0 ? events?.data?.map((note) => (
                                            <TableRow key={note.session_id}>
                                                <InnerCell style={{ color: 'black' }}>
                                                    {note.topic || serviceOptions?.find((a) => a.value === note?.service_name)?.label}
                                                </InnerCell>
                                                <InnerCell>
                                                    {moment(note.session_start_time).format('MMM DD, YYYY')}
                                                </InnerCell>
                                                {!fromMentor && (
                                                    <>
                                                        <InnerCell>
                                                            {`${note.mentor?.user?.first_name} ${note.mentor?.user?.last_name}`}
                                                        </InnerCell>
                                                        <InnerCell style={{ textAlign: 'left' }}>
                                                            {note?.sessionNotes[0]?.description || '--'}
                                                        </InnerCell>
                                                    </>
                                                )}
                                                {fromMentor && (
                                                    <InnerCell>
                                                        30 Min
                                                    </InnerCell>
                                                )}
                                                {fromMentor ? (
                                                    <InnerCell>
                                                        <Box display="flex" gap={1} justifyContent="center">
                                                            {note?.sessionNotes.length > 0 ? (
                                                                <CommonButton
                                                                    label="View Notes"
                                                                    width="150px"
                                                                    startIcon={<Add />}
                                                                    marginBottom="0px"
                                                                    height="30px"
                                                                    handleClick={() => {
                                                                        setSelectedSesion(note);
                                                                        setViewNotes(true);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <CommonButton
                                                                    label="Add Notes"
                                                                    width="150px"
                                                                    startIcon={<Add />}
                                                                    marginBottom="0px"
                                                                    height="30px"
                                                                    handleClick={() => {
                                                                        setSelectedSesion(note);
                                                                        setAddNotes(true);
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </InnerCell>
                                                ) : (
                                                    <InnerCell>
                                                        <Box display="flex" gap={1} justifyContent="center">
                                                            {note?.sessionNotes[0]?.attachment && (
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    sx={{
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => {
                                                                        window.open(`${process.env.REACT_APP_BASE_URL}/file?bname=${note?.sessionNotes[0]?.attachment.url}&download=true`);
                                                                    }}>
                                                                    <DescriptionOutlined sx={{ fontSize: 16 }} />
                                                                    <Typography color="blue" sx={{ cursor: 'pointer' }}>Notes</Typography>
                                                                </Box>
                                                            )}
                                                            {note?.sessionNotes[0]?.other_doc?.length > 0 && (
                                                                <Box
                                                                    onClick={() => {
                                                                        note?.sessionNotes[0]?.other_doc?.map((a) => {
                                                                            window.open(`${process.env.REACT_APP_BASE_URL}/file?bname=${a?.url}&download=true`);
                                                                        })
                                                                    }}>
                                                                    <Typography color="red" sx={{ cursor: 'pointer' }}>Download</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </InnerCell>
                                                )}
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography>No Session Found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </StyledTableContainer >
                        </Box >

                        {/* Pagination Component */}
                        < Box>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                component="div"
                                count={events?.data?.length > 0 ? events?.pagination?.totalPages : 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontSize: '0.875rem',
                                    },
                                }}
                            />
                        </Box >
                    </Fragment >
                }
            />
        </Fragment>
    )
}

export default SessionHistory;